import type { LatLngTuple, MissionRouteGuide, MissionRoutePath } from '@groupe-reca/contracts'
import { buildPolyline, type RoutePolyline } from './pathGeometry'

// Guide reçu avec la mission (figé au démarrage par reca-app-v3, jamais recalculé ici) : parcours validé, dépôt et route
// suivante. Ancienne mission sans guide, parcours non validé, périmé ou expiré : repli explicite, aucun tracé inventé.

export type GuideAvailability =
  | { kind: 'READY'; path: MissionRoutePath; polyline: RoutePolyline }
  | { kind: 'MISSING'; reason: 'ABSENT' | 'NOT_CALCULATED' | 'STALE' | 'EXPIRED' | 'INVALID' }

export function guideAvailability(guide: MissionRouteGuide | null | undefined, now: number): GuideAvailability {
  if (!guide) return { kind: 'MISSING', reason: 'ABSENT' }
  if (guide.status !== 'READY' || !guide.path) {
    return { kind: 'MISSING', reason: guide.status === 'READY' ? 'INVALID' : guide.status }
  }
  // Durée de conservation du fournisseur : au-delà, le parcours n'est plus affiché.
  if (Date.parse(guide.path.expiresAt) <= now) return { kind: 'MISSING', reason: 'EXPIRED' }
  const polyline = buildPolyline(guide.path.legs.map((leg) => leg.geometry))
  if (!polyline) return { kind: 'MISSING', reason: 'INVALID' }
  return { kind: 'READY', path: guide.path, polyline }
}

export const guideMissingLabels: Record<Exclude<GuideAvailability, { kind: 'READY' }>['reason'], string> = {
  ABSENT: 'Parcours non transmis avec cette mission',
  NOT_CALCULATED: 'Parcours non validé par le bureau',
  STALE: 'Parcours à recalculer au bureau (route modifiée)',
  EXPIRED: 'Parcours expiré : à recalculer au bureau',
  INVALID: 'Parcours illisible',
}

/** Index de la résidence dans le parcours; `-1` si elle n'y figure pas (ajoutée, renfort, sans coordonnées). */
export function stopIndexOf(path: MissionRoutePath, serviceLocationId: string): number {
  return path.stops.findIndex((stop) => stop.serviceLocationId === serviceLocationId)
}

/** Distance le long du parcours de la résidence (fin de l'étape qui y mène). */
export function stopAlong(polyline: RoutePolyline, stopIndex: number): number {
  return stopIndex <= 0 ? 0 : (polyline.legEnd[stopIndex - 1] ?? polyline.length)
}

/** Étape qui arrive à la résidence (sens prévu); `null` pour la première. */
export function approachOf(path: MissionRoutePath, serviceLocationId: string): LatLngTuple[] | null {
  return path.legs.find((leg) => leg.toServiceLocationId === serviceLocationId)?.geometry ?? null
}

/**
 * Distance et durée (profil automobile) par le parcours, depuis la position projetée jusqu'à la résidence; `null` si
 * la résidence est derrière (elle se rejoint par « M'y rendre », pas par le parcours) ou absente du parcours.
 */
export function routeRemaining(
  path: MissionRoutePath,
  polyline: RoutePolyline,
  along: number,
  serviceLocationId: string,
): { meters: number; seconds: number } | null {
  const index = stopIndexOf(path, serviceLocationId)
  if (index < 0) return null
  const target = stopAlong(polyline, index)
  if (target < along - 5) return null
  let seconds = 0
  path.legs.forEach((leg, legIndex) => {
    const start = legIndex === 0 ? 0 : (polyline.legEnd[legIndex - 1] ?? 0)
    const end = polyline.legEnd[legIndex] ?? start
    const covered = Math.max(0, Math.min(end, target) - Math.max(start, along))
    if (end > start) seconds += leg.durationSeconds * (covered / (end - start))
  })
  return { meters: Math.max(0, target - along), seconds }
}

export interface Liaison {
  kind: 'ROUTE_TO_ROUTE' | 'ROUTE_TO_DEPOT'
  label: string
  point: { lat: number; lng: number }
}

/**
 * Liaison proposée quand plus aucune résidence n'est à faire : vers la première résidence de la route suivante de
 * l'opérateur (ordre du bureau), sinon retour au dépôt. Le départ (dépôt → première résidence) passe par
 * l'itinéraire suggéré vers la résidence mise en avant. Le tracé est demandé au moment de s'en servir.
 */
export function nextLiaison(guide: MissionRouteGuide | null | undefined, remaining: number): Liaison | null {
  if (remaining > 0 || !guide) return null
  const first = guide.nextRoute?.firstStop
  if (guide.nextRoute && first) {
    return { kind: 'ROUTE_TO_ROUTE', label: `Route suivante : ${guide.nextRoute.name}`, point: { lat: first[0], lng: first[1] } }
  }
  if (guide.depot) {
    return { kind: 'ROUTE_TO_DEPOT', label: `Retour : ${guide.depot.label}`, point: { lat: guide.depot.point[0], lng: guide.depot.point[1] } }
  }
  return null
}

/** Navigation externe (Google Maps) vers un point connu. */
export function navigationToPoint(point: { lat: number; lng: number }): string {
  const params = new URLSearchParams({
    api: '1',
    destination: `${String(point.lat)},${String(point.lng)}`,
    travelmode: 'driving',
    dir_action: 'navigate',
  })
  return `https://www.google.com/maps/dir/?${params.toString()}`
}
